import { CARD_SHAPES, SPECIAL_CARDS } from './constants';

export const createOrderedCards = () => {
  const cards = [];

  const distribution = {
    // 54-card Whot deck (Nigeria): 49 suit cards + 5 WHOT cards.
    // Distribution matches the classic printed deck arrangement.
    [CARD_SHAPES.CIRCLE]: [1, 2, 3, 4, 5, 7, 8, 10, 11, 12, 13, 14],
    [CARD_SHAPES.TRIANGLE]: [1, 2, 3, 4, 5, 7, 8, 10, 11, 12, 13, 14],
    [CARD_SHAPES.CROSS]: [1, 2, 3, 5, 7, 10, 11, 13, 14],
    [CARD_SHAPES.SQUARE]: [1, 2, 3, 5, 7, 10, 11, 13, 14],
    [CARD_SHAPES.STAR]: [1, 2, 3, 4, 5, 7, 8],
  };

  // Ensure each card is unique (no duplicates)
  const seen = new Set();
  Object.entries(distribution).forEach(([shape, numbers]) => {
    (numbers || []).forEach((number) => {
      const cardId = `${shape}-${number}`;
      if (!seen.has(cardId)) {
        cards.push({ number, shape, id: cardId });
        seen.add(cardId);
      }
    });
  });

  for (let i = 0; i < 5; i += 1) {
    cards.push({
      number: SPECIAL_CARDS.WHOT.number,
      shape: SPECIAL_CARDS.WHOT.shape,
      id: `whot-${i}`,
    });
  }

  return cards;
};

export class Deck {
  constructor(options = {}) {
    const seed = options?.seed ?? null;
    this.cards = [];
    this._rand = typeof seed === 'string' && seed.length > 0
      ? createSeededRandom(seed)
      : Math.random;
    this.createDeck();
    this.shuffle();
  }

  createDeck() {
    this.cards = createOrderedCards();
  }

  shuffle() {
    for (let i = this.cards.length - 1; i > 0; i -= 1) {
      const j = Math.floor(this._rand() * (i + 1));
      [this.cards[i], this.cards[j]] = [this.cards[j], this.cards[i]];
    }
    return this.cards;
  }

  draw(num = 1) {
    if (this.cards.length === 0) return [];
    const actualNum = Math.min(num, this.cards.length);
    return this.cards.splice(0, actualNum);
  }

  drawOne() {
    return this.cards.shift() || null;
  }

  remaining() {
    return this.cards.length;
  }

  replenishFromDiscard(discardPile) {
    // Keep the top card on the table.
    // Note: This function receives a COPY of the discard pile, so mutations are safe
    if (!Array.isArray(discardPile) || discardPile.length <= 1) return 0;
    const toRecycle = discardPile.slice(0, -1);
    this.cards.push(...toRecycle);
    this.shuffle();
    return toRecycle.length;
  }
}

function createSeededRandom(seedStr) {
  // xfnv1a -> mulberry32
  let h = 2166136261;
  for (let i = 0; i < seedStr.length; i += 1) {
    h ^= seedStr.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }

  let a = h >>> 0;
  return function mulberry32() {
    a |= 0;
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const isValidMove = (playedCard, topCard, currentShape = null) => {
  if (!playedCard || !topCard) return false;

  // WHOT can be played on anything
  if (playedCard.number === SPECIAL_CARDS.WHOT.number) return true;

  if (playedCard.number === topCard.number) return true;
  if (playedCard.shape === topCard.shape) return true;
  if (currentShape && playedCard.shape === currentShape) return true;

  return false;
};

export const getShapeEmoji = (shape) => {
  switch (shape) {
    case CARD_SHAPES.CIRCLE:
      return '●';
    case CARD_SHAPES.SQUARE:
      return '■';
    case CARD_SHAPES.STAR:
      return '★';
    case CARD_SHAPES.TRIANGLE:
      return '▲';
    case CARD_SHAPES.CROSS:
      return '✚';
    default:
      return '?';
  }
};

export const getShapeColor = (shape) => {
  switch (shape) {
    case CARD_SHAPES.CIRCLE:
      return '#3b82f6';
    case CARD_SHAPES.SQUARE:
      return '#10b981';
    case CARD_SHAPES.STAR:
      return '#f59e0b';
    case CARD_SHAPES.TRIANGLE:
      return '#ef4444';
    case CARD_SHAPES.CROSS:
      return '#8b5cf6';
    default:
      return '#64748b';
  }
};